import type { Order } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth';
import { api, ApiError } from '../api';
import { IconBack, IconNext, IconPlus } from '../components/icons';
import { Empty, ErrorBox, PageHead, QueryView, StatusBadge } from '../components/ui';
import { addDays, formatDate, formatMoney, formatQty, isIsoDate, todayIso } from '../format';
import { useDebounced, useDesktop, useSearchParam } from '../hooks';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

/** When the order was entered, in Denver: the time on the same day, the date and time on another. */
export function enteredAt(createdAt: string, today: string, locale: string): string {
  const d = new Date(createdAt);
  const day = d.toLocaleDateString('en-CA', { timeZone: 'America/Denver' });
  const time = d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' });
  return day === today ? time : `${d.toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'America/Denver' })}, ${time}`;
}

const STATUSES = ['draft', 'confirmed', 'picked', 'out_for_delivery', 'delivered', 'invoiced', 'cancelled'] as const;
type OrderSort = 'no' | 'customer' | 'date' | 'status' | 'channel' | 'lines' | 'unpriced' | 'total' | 'created';

export function OrdersPage() {
  const desktop = useDesktop();
  return desktop ? <OrdersSheet /> : <OrdersList />;
}

/** The day's sales orders on the sheet: status filter with counts, value, unpriced lines; Enter opens the order. */
function OrdersSheet() {
  const { t, intlLocale } = useLocale();
  const navigate = useNavigate();
  const [dateParam, setDate] = useSearchParam('date');
  const [statusParam, setStatus] = useSearchParam('status');
  const status = STATUSES.find((x) => x === statusParam);
  const [q, setQ] = useState('');
  const today = todayIso();
  const date = isIsoDate(dateParam) ? dateParam : today;
  const list = useQuery({ queryKey: qk.orders(date), queryFn: ({ signal }) => api.orders(date, signal) });
  // a name or a number typed searches every day, not just the one on screen (C6)
  const term = useDebounced(q.trim());
  const searching = term.length >= 2 || /^#?\d+$/.test(term);
  const found = useQuery({ queryKey: [...qk.orderLists, 'find', term], queryFn: ({ signal }) => api.findOrders(term, signal), enabled: searching });
  const unpriced = (o: Order) => o.lines.filter((l) => l.unitPrice === null).length;
  const sorters = useMemo(
    () => ({
      no: byNumber<Order>((o) => o.orderNo),
      customer: byText<Order>((o) => o.customerName),
      date: byText<Order>((o) => o.requestedDate),
      status: byNumber<Order>((o) => STATUSES.indexOf(o.status)),
      channel: byText<Order>((o) => o.channel),
      lines: byNumber<Order>((o) => o.lines.length),
      unpriced: byNumber<Order>((o) => unpriced(o)),
      total: byNumber<Order>((o) => o.total),
      created: byText<Order>((o) => o.createdAt),
    }),
    [],
  );
  const { sorted, head } = useSort<Order, OrderSort>(sorters, { key: 'no', dir: -1 }, ['no', 'date', 'lines', 'unpriced', 'total', 'created']);
  const all = list.data ?? [];
  const words = q.trim().toLowerCase();
  const rows = searching
    ? sorted((found.data ?? []).filter((o) => !status || o.status === status))
    : sorted(all.filter((o) => (!status || o.status === status) && (!words || o.customerName.toLowerCase().includes(words) || String(o.orderNo).includes(words))));
  // the strip is the whole day, whatever the filter or the search shows
  const live = all.filter((o) => o.status !== 'cancelled');
  const value = Math.round(live.reduce((s, o) => s + o.total, 0) * 100) / 100;
  const unpricedOrders = live.filter((o) => unpriced(o) > 0).length;
  const countOf = (x: (typeof STATUSES)[number]) => all.filter((o) => o.status === x).length;
  const columns: SheetColumn<Order>[] = [
    { key: 'no', header: head('no', t('orderNo')), label: t('orderNo'), width: '84px', align: 'right', render: (o) => <Link to={`/orders/${o.id}`}>#{o.orderNo}</Link> },
    { key: 'customer', header: head('customer', t('customer')), label: t('customer'), width: '240px', render: (o) => o.customerName },
    ...(searching ? [{ key: 'date', header: head('date', t('deliveryDate')), label: t('deliveryDate'), width: '120px', render: (o: Order) => formatDate(o.requestedDate, intlLocale, 'date') }] : []),
    { key: 'status', header: head('status', t('colStatus')), label: t('colStatus'), width: '132px', render: (o) => <StatusBadge status={o.status} /> },
    { key: 'channel', header: head('channel', t('channel')), label: t('channel'), width: '104px', render: (o) => t(`channel_${o.channel}`) },
    { key: 'lines', header: head('lines', t('linesCol')), label: t('linesCol'), width: '72px', align: 'right', render: (o) => String(o.lines.length) },
    { key: 'unpriced', header: head('unpriced', t('unpricedCol')), label: t('unpricedCol'), width: '88px', align: 'right', render: (o) => (unpriced(o) ? String(unpriced(o)) : ''), cellClass: (o) => (unpriced(o) && o.status !== 'cancelled' ? 'warn' : undefined) },
    { key: 'total', header: head('total', t('total')), label: t('total'), width: '112px', align: 'right', render: (o) => <strong>{formatMoney(o.total, intlLocale)}</strong>, cellClass: (o) => (o.status === 'cancelled' ? 'struck' : undefined) },
    { key: 'created', header: head('created', t('createdCol')), label: t('createdCol'), width: '120px', render: (o) => enteredAt(o.createdAt, today, intlLocale) },
  ];

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('navSalesOrders')}</h1>
          <p className="muted">{t('ordersHint')}</p>
        </div>
        <Link to={`/orders/new?date=${date}`} className="btn btn-primary btn-sm">
          {t('newOrder')}
        </Link>
      </header>
      <div className="toolbar">
        <button type="button" className="btn btn-secondary btn-sm btn-arrow" aria-label={t('prevDay')} onClick={() => setDate(addDays(date, -1))}>
          <IconBack />
        </button>
        <label className="tool">
          <span>{t('deliveryDate')}</span>
          <input type="date" value={date} onChange={(e) => setDate(isIsoDate(e.target.value) ? e.target.value : '')} />
        </label>
        <button type="button" className="btn btn-secondary btn-sm btn-arrow" aria-label={t('nextDay')} onClick={() => setDate(addDays(date, 1))}>
          <IconNext />
        </button>
        {date !== today ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDate('')}>
            {t('today')}
          </button>
        ) : null}
        <div className="seg" role="group" aria-label={t('colStatus')}>
          <button type="button" aria-pressed={!status} onClick={() => setStatus('')}>
            {t('stateAll')} {all.length}
          </button>
          {STATUSES.filter((x) => countOf(x) > 0 || x === status).map((x) => (
            <button key={x} type="button" aria-pressed={status === x} onClick={() => setStatus(x)}>
              {t(`status_${x}`)} {countOf(x)}
            </button>
          ))}
        </div>
        <input className="tool-search" type="search" placeholder={t('findOrder')} aria-label={t('findOrder')} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <dl className="totals-strip" data-testid="orders-strip">
        <div>
          <dt>{t('ordersCol')}</dt>
          <dd>{live.length}</dd>
        </div>
        <div>
          <dt>{t('orderValue')}</dt>
          <dd>{formatMoney(value, intlLocale)}</dd>
        </div>
        <div className={unpricedOrders > 0 ? 'warn' : undefined}>
          <dt>{t('unpricedCol')}</dt>
          <dd>{unpricedOrders}</dd>
        </div>
        <div>
          <dt>{t('deliveryDate')}</dt>
          <dd>{formatDate(date, intlLocale, 'date')}</dd>
        </div>
      </dl>
      {searching ? <p className="muted small" data-testid="orders-every-day">{t('ordersEveryDay')}</p> : null}
      <QueryView query={searching ? found : list}>
        {() =>
          rows.length === 0 ? (
            <p className="muted pick-first">{t('noOrders')}</p>
          ) : (
            <Sheet label={t('navSalesOrders')} columns={columns} rows={rows} rowKey={(o) => o.id} rowLabel={(o) => `#${o.orderNo} ${o.customerName}`} minWidth="960px" onOpen={(o) => navigate(`/orders/${o.id}`)} rowClass={(o) => (o.status === 'cancelled' ? 'zero' : undefined)} />
          )
        }
      </QueryView>
    </div>
  );
}

/** The phone's day of orders: a date bar, a row per order, the big New order button. */
function OrdersList() {
  const { t, intlLocale } = useLocale();
  const [dateParam, setDate] = useSearchParam('date');
  const today = todayIso();
  const date = isIsoDate(dateParam) ? dateParam : today;
  const list = useQuery({ queryKey: qk.orders(date), queryFn: ({ signal }) => api.orders(date, signal) });

  return (
    <div className="stack">
      <h1>{t('navSalesOrders')}</h1>
      <div className="datebar">
        <button type="button" className="icon-btn" aria-label={t('prevDay')} onClick={() => setDate(addDays(date, -1))}>
          <IconBack />
        </button>
        <input
          type="date"
          aria-label={t('deliveryDate')}
          value={date}
          onChange={(e) => setDate(isIsoDate(e.target.value) ? e.target.value : '')}
        />
        <button type="button" className="icon-btn" aria-label={t('nextDay')} onClick={() => setDate(addDays(date, 1))}>
          <IconNext />
        </button>
      </div>
      <div className="date-caption">
        <span className="muted">{formatDate(date, intlLocale, 'long')}</span>
        {date === today ? (
          <span className="badge badge-ok">{t('today')}</span>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={() => setDate('')}>
            {t('today')}
          </button>
        )}
      </div>

      <QueryView query={list}>
        {(orders) =>
          orders.length === 0 ? (
            <Empty>{t('noOrders')}</Empty>
          ) : (
            <ul className="list">
              {orders.map((o) => (
                <li key={o.id}>
                  <Link to={`/orders/${o.id}`} className="row">
                    <span className="row-main">
                      <span className="row-title">
                        #{o.orderNo} {o.customerName}
                      </span>
                      <span className="row-sub">
                        <StatusBadge status={o.status} />
                        {o.lines.length} {t('lines')}
                      </span>
                    </span>
                    <span className="row-end">
                      <span className="row-end-value">{formatMoney(o.total, intlLocale)}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>

      <Link to={`/orders/new?date=${date}`} className="btn btn-primary btn-lg fab">
        <IconPlus /> {t('newOrder')}
      </Link>
    </div>
  );
}

/** The office turns a delivered (or any live) order into its invoice; afterwards the invoice is a tap away. */
function InvoiceAction({ order }: { order: Order }) {
  const { t } = useLocale();
  const { me } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const existing = useQuery({
    queryKey: qk.invoices(`order:${order.id}`),
    queryFn: ({ signal }) => api.invoices({ status: 'issued', customer: order.customerId }, signal).then((rows) => rows.filter((i) => i.orderId === order.id)),
    enabled: order.status === 'invoiced',
  });
  const make = useMutation({
    mutationFn: () => api.invoiceOrder(order.id),
    onSuccess: (inv) => {
      qc.setQueryData(qk.invoice(inv.id), inv);
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/invoices/${inv.id}`);
    },
  });
  const books = me?.role === 'owner' || me?.role === 'office';
  if (order.status === 'invoiced') {
    const inv = existing.data?.[0];
    return inv ? (
      <Link to={`/invoices/${inv.id}`} className="btn btn-secondary btn-lg btn-block">
        {t('invoice')} {inv.invoiceNo}
      </Link>
    ) : null;
  }
  if (!books || order.status === 'cancelled') return null;
  const unpriced = order.lines.some((l) => l.unitPrice === null);
  return (
    <div className="stack-sm">
      {unpriced ? <p className="muted">{t('unpricedLines')}</p> : null}
      <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => make.mutate()} disabled={make.isPending || unpriced}>
        {make.isPending ? t('sending') : t('makeInvoice')}
      </button>
      {make.error ? <ErrorBox error={make.error} /> : null}
    </div>
  );
}

/** The office moves an order along (confirm, on the road, delivered) or cancels it with a reason. */
function StatusActions({ order }: { order: Order }) {
  const { t } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const move = useMutation({
    mutationFn: (v: { status: Order['status']; reason?: string; overrideHold?: boolean }) => api.setOrderStatus(order.id, v.status, v.reason, v.overrideHold),
    onSuccess: (updated) => {
      qc.setQueryData(qk.order(order.id), updated);
      setCancelling(false);
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['stock'] });
    },
  });
  const role = me?.role;
  const office = role === 'owner' || role === 'office';
  const crew = office || role === 'warehouse' || role === 'driver';
  const confirmText = useMutation({ mutationFn: () => api.textOrderConfirmation(order.id) });
  if (!crew || order.status === 'invoiced' || order.status === 'cancelled') return null;
  const next: { status: Order['status']; label: 'confirmOrder' | 'pickedBtn' | 'markOut' | 'markDelivered' } | null =
    order.status === 'draft' && office ? { status: 'confirmed', label: 'confirmOrder' }
    : order.status === 'confirmed' ? { status: 'picked', label: 'pickedBtn' }
    : order.status === 'picked' ? { status: 'out_for_delivery', label: 'markOut' }
    : order.status === 'out_for_delivery' ? { status: 'delivered', label: 'markDelivered' }
    : null;
  return (
    <div className="stack-sm">
      {next ? (
        <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => move.mutate({ status: next.status })} disabled={move.isPending}>
          ✓ {t(next.label)}
        </button>
      ) : null}
      {office && order.status !== 'draft' ? (
        confirmText.data ? (
          <p className="note" role="status">
            ✓ {t('textSent')}
          </p>
        ) : (
          <button type="button" className="btn btn-secondary btn-block" onClick={() => confirmText.mutate()} disabled={confirmText.isPending}>
            {t('textConfirmation')}
          </button>
        )
      ) : null}
      {confirmText.error ? <ErrorBox error={confirmText.error} /> : null}
      {/* the office cancels; an order already delivered is the owner's to cancel (decision 181) */}
      {office && (order.status !== 'delivered' || role === 'owner') ? (
        cancelling ? (
          <form
            className="stack-sm"
            onSubmit={(e) => {
              e.preventDefault();
              if (reason.trim()) move.mutate({ status: 'cancelled', reason: reason.trim() });
            }}
          >
            <label className="field">
              <span>{t('cancelWhy')}</span>
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
            </label>
            <div className="picker-head">
              <button type="button" className="btn btn-secondary" onClick={() => setCancelling(false)}>
                {t('back')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={move.isPending || !reason.trim()}>
                {t('cancelOrder')}
              </button>
            </div>
          </form>
        ) : (
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setCancelling(true)}>
            {t('cancelOrder')}
          </button>
        )
      ) : null}
      {move.error ? <ErrorBox error={move.error} /> : null}
      {move.error instanceof ApiError && move.error.status === 409 && role === 'owner' && order.status === 'draft' ? (
        <button type="button" className="btn btn-secondary btn-block" onClick={() => move.mutate({ status: 'confirmed', overrideHold: true })} disabled={move.isPending}>
          {t('takeAnyway')}
        </button>
      ) : null}
    </div>
  );
}

export function OrderDetailPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const { id = '' } = useParams();
  const location = useLocation();
  const qc = useQueryClient();
  const justSaved = (location.state as { saved?: unknown } | null)?.saved === true;
  const [showSaved, setShowSaved] = useState(justSaved);

  useEffect(() => {
    if (!justSaved) return;
    const timer = setTimeout(() => setShowSaved(false), 5000);
    return () => clearTimeout(timer);
  }, [justSaved]);

  const query = useQuery({
    queryKey: qk.order(id),
    queryFn: ({ signal }) => api.order(id, signal),
    // A just-saved order is already in the cache; otherwise start from the day list we have.
    initialData: () =>
      qc
        .getQueriesData<Order[]>({ queryKey: qk.orderLists })
        .flatMap(([, rows]) => rows ?? [])
        .find((o) => o.id === id),
    initialDataUpdatedAt: 0,
  });

  return (
    <div className="stack">
      {showSaved ? (
        <div className="flash" role="status">
          ✓ {t('saved')}
        </div>
      ) : null}
      <QueryView query={query}>
        {(o) => (
          <>
            <PageHead title={`${t('order')} #${o.orderNo}`} back={`/orders?date=${o.requestedDate}`} />
            <section className="card">
              <span className="row-title big">{o.customerName}</span>
              <span className="badges">
                <StatusBadge status={o.status} />
                <span className="badge">{t(`channel_${o.channel}`)}</span>
              </span>
              <span>
                <span className="muted">{t('deliveryDate')}: </span>
                <strong>{formatDate(o.requestedDate, intlLocale, 'long')}</strong>
              </span>
            </section>

            <ul className="list">
              {o.lines.map((l) => (
                <li key={l.id} className="row">
                  <span className="qty-pill">
                    {formatQty(l.qty)} {l.unit}
                  </span>
                  <span className="row-main">
                    <span className="row-title">{l.itemName}</span>
                    <span className="row-sub">
                      {l.unitPrice != null ? formatMoney(l.unitPrice, intlLocale) : t('noPrice')}
                      {l.note ? ` · ${l.note}` : ''}
                    </span>
                  </span>
                  <span className="row-end-value">
                    {l.unitPrice != null ? formatMoney(Math.round(l.qty * l.unitPrice * 100) / 100, intlLocale) : '—'}
                  </span>
                </li>
              ))}
            </ul>

            <div className="total-line">
              <span>{t('total')}</span>
              <strong>{formatMoney(o.total, intlLocale)}</strong>
            </div>

            {o.notes ? (
              <section className="card">
                <h2>{t('notes')}</h2>
                <p className="pre">{o.notes}</p>
              </section>
            ) : null}

            {/* C6: an order is changed until it is picked (lines, date, notes) by those who take orders */}
            {['draft', 'confirmed'].includes(o.status) && ['owner', 'office', 'sales'].includes(me?.role ?? '') ? (
              <Link to={`/orders/${o.id}/edit`} className="btn btn-secondary btn-lg btn-block" data-testid="change-order">
                {t('changeOrder')}
              </Link>
            ) : null}
            <StatusActions order={o} />
            <InvoiceAction order={o} />
          </>
        )}
      </QueryView>
    </div>
  );
}
